/**
 * Modal de **guardar optimización** (réplica de `openSaveModal` del SSR).
 *
 * Nombre y notas opcionales, validados con Zod (`saveCutlistSchema`) antes de delegar.
 * Al enviar llama a `useSaveCutlistJob` (mutación que invalida el historial) con el
 * snapshot completo (piezas, tableros, config, resultado y stats). El error de la API
 * (`{error_code, message}`) se traduce a mensaje legible. Controlado por `open` del
 * padre; presentacional + orquestación de un hook de datos (no hace `fetch`).
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import type { SaveCutlistJobBody } from "../api";
import { cutlistErrorMessage } from "../format";
import { useSaveCutlistJob } from "../hooks/useCutlist";
import { saveCutlistSchema } from "../schemas";
import type { CutConfig, OptimizeResult, Part, Stock } from "../types";

interface SaveJobDialogProps {
  open: boolean;
  onClose: () => void;
  /** Snapshot a guardar (la entrada y el resultado actuales del editor). */
  snapshot: {
    parts: Part[];
    stocks: Stock[];
    config: CutConfig;
    result: OptimizeResult;
  };
  /** Aviso al guardar con éxito (p. ej. refrescar el historial si está abierto). */
  onSaved: () => void;
}

/** Errores por campo (de Zod). */
type FieldErrors = Partial<Record<"nombre" | "notas", string>>;

export function SaveJobDialog({ open, onClose, snapshot, onSaved }: SaveJobDialogProps) {
  const save = useSaveCutlistJob();
  const [nombre, setNombre] = useState("");
  const [notas, setNotas] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Al abrir, limpia el formulario y el estado de la mutación.
  useEffect(() => {
    if (open) {
      setNombre("");
      setNotas("");
      setFieldErrors({});
      save.reset();
    }
    // `save` es estable por render de TanStack Query; solo nos interesa `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const result = saveCutlistSchema.safeParse({ nombre, notas });
    if (!result.success) {
      const flattened = result.error.flatten().fieldErrors;
      const errors: FieldErrors = {};
      const nombreError = flattened.nombre?.[0];
      const notasError = flattened.notas?.[0];
      if (nombreError) {
        errors.nombre = nombreError;
      }
      if (notasError) {
        errors.notas = notasError;
      }
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const body: SaveCutlistJobBody = {
      nombre: result.data.nombre ? result.data.nombre : null,
      notas: result.data.notas ? result.data.notas : null,
      parts: snapshot.parts,
      stocks: snapshot.stocks,
      config: snapshot.config,
      result: snapshot.result,
      stats: snapshot.result.stats,
    };

    save.mutate(body, {
      onSuccess: () => {
        onSaved();
        onClose();
      },
    });
  }

  const apiError = save.isError ? cutlistErrorMessage(save.error) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>Guardar optimización</DialogTitle>
        <DialogDescription>
          Queda en el historial de tu sede para recargarla más tarde.
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="cl-save-name" className="text-sm font-medium text-slate-700">
              Nombre (opcional)
            </label>
            <Input
              id="cl-save-name"
              autoComplete="off"
              placeholder="Ej. Cocina cliente Pérez"
              value={nombre}
              aria-invalid={fieldErrors.nombre ? true : undefined}
              aria-describedby={fieldErrors.nombre ? "cl-save-name-error" : undefined}
              onChange={(e) => setNombre(e.target.value)}
            />
            {fieldErrors.nombre ? (
              <p id="cl-save-name-error" className="text-sm text-red-600">
                {fieldErrors.nombre}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="cl-save-notes" className="text-sm font-medium text-slate-700">
              Notas (opcional)
            </label>
            <textarea
              id="cl-save-notes"
              rows={3}
              placeholder="Observaciones…"
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={notas}
              aria-invalid={fieldErrors.notas ? true : undefined}
              aria-describedby={fieldErrors.notas ? "cl-save-notes-error" : undefined}
              onChange={(e) => setNotas(e.target.value)}
            />
            {fieldErrors.notas ? (
              <p id="cl-save-notes-error" className="text-sm text-red-600">
                {fieldErrors.notas}
              </p>
            ) : null}
          </div>

          {apiError ? (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={save.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
